import type { NpoVideoPlayer } from '@npo/player';
import { newTag, newPageTracker, newGovoltePlugin } from '@npotag/tag';
import type { TPlayerStatus } from '~~/types/local_types';

type ViewMode = 'inline' | 'fullscreen' | 'pictureinpicture';

const displayModeToViewMode: Record<string, ViewMode> = {
  inline: 'inline',
  fullscreen: 'fullscreen',
  'picture-in-picture': 'pictureinpicture',
};

export function useNpoPlayerSetup(): {
  destroyPlayer: () => void;
  initializePlayer: (
    container: HTMLElement | undefined,
    initialState: TPlayerStatus,
    durationLabel?: string,
  ) => Promise<NpoVideoPlayer | undefined>;
  play: () => void;
  pause: () => void;
  currentState: Ref<TPlayerStatus>;
  currentViewMode: Ref<ViewMode | undefined>;
} {
  let player: NpoVideoPlayer | undefined;
  const config = useRuntimeConfig();

  const {
    NpoTagConfigConfigurationsBrand,
    NpoTagConfigConfigurationsBrandId,
    NpoTagConfigConfigurationsDebug,
  } = config.public;

  const currentState = ref<'playing' | 'paused'>('paused');
  const currentViewMode = ref<ViewMode | undefined>('inline');

  const initializePlayer = async (
    container: HTMLElement | undefined,
    initialState: TPlayerStatus = 'paused',
    durationLabel?: string,
  ): Promise<NpoVideoPlayer | undefined> => {
    const { $loadNpoPlayer, $loadNpoPlayerUi } = useNuxtApp();

    if (!container) {
      console.warn('useNpoPlayerSetup: no container found.');
      return;
    }

    // load the NPO player css
    import('@npo/player-video-ui/dist/style.css');

    const [playerModule, uiModule] = await Promise.all([$loadNpoPlayer(), $loadNpoPlayerUi()]);

    if (!playerModule || !uiModule) return;

    const { NpoVideoPlayer: NpoVideoPlayerClass, MediaType, PlayerEventNames } = playerModule;
    const { NpoVideoPlayerUIFactory } = uiModule;

    /**
     * Adds the duration badge to the big center play button as part of the UI's
     * own construction, nested inside the native play button (so it inherits the
     * button's own show/hide-on-play behaviour for free, no extra JS wiring needed).
     */
    class NpoVideoPlayerUIFactoryWithDuration extends NpoVideoPlayerUIFactory {
      constructor(private readonly durationLabel?: string) {
        super();
      }

      createMiddleBar(parentElement?: HTMLElement): HTMLElement {
        const middleBar = super.createMiddleBar(parentElement);
        if (this.durationLabel) {
          const playButton = middleBar.querySelector('.npoplayer-play-button');
          if (playButton && !playButton.querySelector('.duration')) {
            const label = document.createElement('div');
            label.className = 'duration';
            label.textContent = this.durationLabel;
            playButton.appendChild(label);
          }
        }
        return middleBar;
      }
    }

    const initProps = {
      brand: NpoTagConfigConfigurationsBrand,
      brand_id: parseInt(NpoTagConfigConfigurationsBrandId, 10),
      platform: 'site',
      platform_version: 'none',
      debug: !!NpoTagConfigConfigurationsDebug,
    };

    let npoTagConfig: { initprops: typeof initProps; pageTracker?: unknown; npoTag?: unknown } = {
      initprops: initProps,
    };
    try {
      const npoTagInstance = newTag(initProps, [newGovoltePlugin()]);
      if (npoTagInstance) {
        npoTagConfig = {
          initprops: initProps,
          pageTracker: newPageTracker(npoTagInstance, {}),
          npoTag: npoTagInstance,
        };
      }
    } catch (error) {
      console.error('Failed to initialize NPO Tag:', error);
    }

    player = new NpoVideoPlayerClass(
      {
        options: {
          enableKeyboardControls: true,
          preload: true,
          autoplay: initialState === 'playing',
        },
        mediaType: MediaType.VIDEO,
        npoTag: npoTagConfig,
        uiFactory: new NpoVideoPlayerUIFactoryWithDuration(durationLabel),
      },
      container,
    );

    player.on(PlayerEventNames.Play, () => {
      currentState.value = 'playing';
    });

    player.on(PlayerEventNames.Pause, () => {
      currentState.value = 'paused';
    });

    player.player?.on(PlayerEventNames.DisplayModeChanged, () => {
      const displayMode = player?.player?.state.displayMode;
      currentViewMode.value = displayMode ? displayModeToViewMode[displayMode] : 'inline';
    });

    return player;
  };

  const destroyPlayer = () => {
    player?.destroy();
    player = undefined;
  };

  const play = () => {
    if (!player) return;
    player?.play();
    currentState.value = 'playing';
  };

  const pause = () => {
    if (!player) return;
    player?.pause();
    currentState.value = 'paused';
  };

  return {
    initializePlayer,
    destroyPlayer,
    play,
    pause,
    currentState,
    currentViewMode,
  };
}
